import { useCallback, useMemo, useState } from "react";
import { Button, Checkbox, type Column } from "@legal-500/legal500-design-system";
import { PageHeader } from "../../../components/PageHeader/index.js";
import { ConfirmationModal } from "../../../components/Modal/ConfirmationModal.js";
import { DataTable } from "../../../components/DataTable/DataTable.js";
import { usePermissions } from "../../../hooks/usePermissions.js";
import {
  APPLY_CHUNK_SIZE,
  nameTranslationsImportService,
  type ApplyRowResult,
  type ApplyRowStatus,
  type ScanResponse,
  type ScanRowStatus,
} from "../../../api/nameTranslationsImport.service.js";
import { downloadPlannedCsv, downloadResultsCsv } from "./importCsv.js";
import { runChunkedApply } from "./runApply.js";
import styles from "./NameTranslationsImport.module.css";

type Phase = "idle" | "scanning" | "scanned" | "applying" | "done";

type ApplyProgress = {
  done: number;
  total: number;
};

type TableRow = {
  id: string;
  index: number;
  position: number;
  entityType: string;
  entityName: string;
  termId: string;
  localeCode: string;
  translatedName: string;
  status: ScanRowStatus;
  selectable: boolean;
  selected: boolean;
};

const STATUS_LABEL: Record<ScanRowStatus, string> = {
  ready: "Ready",
  exists: "Exists in Address Book",
  alias: "Merged-away WP term",
  deleted: "Entity deleted",
  missing: "No Address Book row",
};

const STATUS_PILL: Record<ScanRowStatus, string> = {
  ready: styles.pillOk,
  exists: styles.pillNeutral,
  alias: styles.pillWarning,
  deleted: styles.pillError,
  missing: styles.pillError,
};

const RESULT_TILES: { status: ApplyRowStatus; label: string; tile: string }[] = [
  { status: "applied", label: "Applied", tile: styles.tileOk },
  {
    status: "skipped_exists",
    label: "Skipped — already had a translation",
    tile: styles.tileNeutral,
  },
  { status: "not_found", label: "Not found", tile: styles.tileNeutral },
  { status: "error", label: "Error", tile: styles.tileError },
];

function countByStatus(results: ApplyRowResult[]): (status: ApplyRowStatus) => number {
  const tally = new Map<ApplyRowStatus, number>();
  for (const result of results) {
    tally.set(result.status, (tally.get(result.status) ?? 0) + 1);
  }
  return status => tally.get(status) ?? 0;
}

// Indeterminate is a DOM property, not an attribute, so it goes through the ref.
function SelectAllCheckbox({
  checked,
  indeterminate,
  disabled,
  onChange,
}: {
  checked: boolean;
  indeterminate: boolean;
  disabled: boolean;
  onChange: () => void;
}) {
  const setInput = useCallback(
    (input: HTMLInputElement | null) => {
      if (input) {
        input.indeterminate = indeterminate;
      }
    },
    [indeterminate],
  );
  return (
    <Checkbox
      ref={setInput}
      aria-label="Select all ready rows"
      checked={checked}
      disabled={disabled}
      onChange={onChange}
    />
  );
}

export default function NameTranslationsImport() {
  const { hasPermission } = usePermissions();

  const [phase, setPhase] = useState<Phase>("idle");
  const [scan, setScan] = useState<ScanResponse | null>(null);
  const [selected, setSelected] = useState<ReadonlySet<number>>(() => new Set());
  const [error, setError] = useState<string | null>(null);
  const [isConfirming, setIsConfirming] = useState(false);
  const [applyProgress, setApplyProgress] = useState<ApplyProgress>({ done: 0, total: 0 });
  const [applied, setApplied] = useState<ApplyRowResult[] | null>(null);

  const canUse = hasPermission("developer.write");

  const runScan = useCallback(async () => {
    setError(null);
    setApplied(null);
    setScan(null);
    setSelected(new Set());
    setPhase("scanning");
    try {
      const response = await nameTranslationsImportService.scan();
      setScan(response);
      setSelected(
        new Set(response.rows.flatMap((row, index) => (row.status === "ready" ? [index] : []))),
      );
      setPhase("scanned");
    } catch (scanError) {
      setError(scanError instanceof Error ? scanError.message : String(scanError));
      setPhase("idle");
    }
  }, []);

  const toggleRow = useCallback((index: number) => {
    setSelected(current => {
      const next = new Set(current);
      if (!next.delete(index)) {
        next.add(index);
      }
      return next;
    });
  }, []);

  const setRows = useCallback((indexes: number[], isSelected: boolean) => {
    setSelected(current => {
      const next = new Set(current);
      for (const index of indexes) {
        if (isSelected) {
          next.add(index);
        } else {
          next.delete(index);
        }
      }
      return next;
    });
  }, []);

  const scanRows = scan?.rows ?? [];

  const selectedRows = useMemo(
    () => scanRows.filter((row, index) => row.status === "ready" && selected.has(index)),
    [scanRows, selected],
  );
  const selectedCount = selectedRows.length;
  const readyCount = useMemo(
    () => scanRows.filter(row => row.status === "ready").length,
    [scanRows],
  );

  const downloadPlanned = useCallback(() => {
    downloadPlannedCsv(selectedRows, new Date());
  }, [selectedRows]);

  const applyImport = useCallback(async () => {
    setIsConfirming(false);
    setError(null);
    setPhase("applying");

    const { results, failure } = await runChunkedApply(
      selectedRows.map(row => ({
        entityType: row.entityType,
        entityId: row.entityId,
        localeCode: row.localeCode,
        translatedName: row.translatedName,
      })),
      (done, total) => setApplyProgress({ done, total }),
    );

    if (results.length === 0 && failure) {
      setError(failure);
      setPhase("scanned");
      return;
    }
    setApplied(results);
    setError(failure);
    setPhase("done");
  }, [selectedRows]);

  const tableRows = useMemo<TableRow[]>(
    () =>
      scanRows.map((row, index) => ({
        id: `${index}-${row.entityType}-${row.entityId}-${row.localeCode}`,
        index,
        position: index + 1,
        entityType: row.entityType,
        entityName: row.entityName,
        termId: row.termId,
        localeCode: row.localeCode,
        translatedName: row.translatedName,
        status: row.status,
        selectable: row.status === "ready",
        selected: selected.has(index),
      })),
    [scanRows, selected],
  );

  const selectableRows = useMemo(() => tableRows.filter(row => row.selectable), [tableRows]);
  const allSelected = selectableRows.length > 0 && selectedCount === selectableRows.length;

  const columns = useMemo<Column<TableRow>[]>(
    () => [
      {
        header: (
          <SelectAllCheckbox
            checked={allSelected}
            indeterminate={selectedCount > 0 && !allSelected}
            disabled={selectableRows.length === 0}
            onChange={() =>
              setRows(
                selectableRows.map(row => row.index),
                !allSelected,
              )
            }
          />
        ) as unknown as string,
        accessor: "selected",
        render: (_value, row) =>
          row.selectable ? (
            <Checkbox
              aria-label={`Import row ${row.position} — ${row.entityName} (${row.localeCode})`}
              checked={row.selected}
              onChange={() => toggleRow(row.index)}
            />
          ) : (
            <span className={styles.mono} aria-hidden="true">
              —
            </span>
          ),
      },
      {
        header: "Type",
        accessor: "entityType",
        render: (_value, row) => <span className={styles.mono}>{row.entityType}</span>,
      },
      {
        header: "Entity",
        accessor: "entityName",
        wrap: true,
        render: (_value, row) => <span className={styles.name}>{row.entityName || "—"}</span>,
      },
      {
        header: "WP term",
        accessor: "termId",
        render: (_value, row) => <span className={styles.mono}>{row.termId}</span>,
      },
      {
        header: "Locale",
        accessor: "localeCode",
        render: (_value, row) => <span className={styles.mono}>{row.localeCode}</span>,
      },
      {
        header: "Translated name",
        accessor: "translatedName",
        wrap: true,
        render: (_value, row) => <span className={styles.name}>{row.translatedName}</span>,
      },
      {
        header: "Status",
        accessor: "status",
        render: (_value, row) => (
          <span className={`${styles.pill} ${STATUS_PILL[row.status]}`}>
            {STATUS_LABEL[row.status]}
          </span>
        ),
      },
    ],
    [allSelected, selectedCount, selectableRows, setRows, toggleRow],
  );

  const resultCounts = useMemo(() => countByStatus(applied ?? []), [applied]);

  if (!canUse) {
    return (
      <div className={styles.page}>
        <div className={styles.accessDenied} role="alert">
          <h2>Access Denied</h2>
          <p>You do not have permission to view the Name Translations Import tool.</p>
        </div>
      </div>
    );
  }

  const isBusy = phase === "scanning" || phase === "applying";
  const unusableCount = scan
    ? scan.counts.byStatus.deleted +
      scan.counts.byStatus.missing +
      scan.counts.blank +
      scan.counts.invalidLocale
    : 0;

  return (
    <div className={styles.page}>
      <PageHeader
        title="Name Translations Import"
        subtitle="Copy WordPress office and lawyer name translations into Address Book"
      />

      {error ? (
        <div className={`${styles.banner} ${styles.bannerError}`} role="alert">
          {error}
        </div>
      ) : null}

      <div className={styles.card}>
        <div className={styles.cardTitle}>1 · Scan WordPress translations</div>
        <div className={styles.toolbar}>
          <Button variant="contained" disabled={isBusy} onClick={() => void runScan()}>
            Scan
          </Button>
          <span className={styles.rowNote}>
            Read-only: matches every WordPress translation to an Address Book office or lawyer.
            Nothing is written until you apply. Existing Address Book translations are never
            overwritten.
          </span>
        </div>
      </div>

      {phase === "scanning" ? (
        <div className={styles.card}>
          <div className={styles.cardTitle}>Scanning…</div>
        </div>
      ) : null}

      {phase === "applying" ? (
        <div className={styles.card}>
          <div className={styles.cardTitle}>Importing…</div>
          <div className={styles.progress} role="status" aria-live="polite">
            {`${applyProgress.done.toLocaleString()} / ${applyProgress.total.toLocaleString()} rows`}
          </div>
          <div className={styles.rowNote}>
            {applyProgress.total > APPLY_CHUNK_SIZE
              ? `Sent in batches of ${APPLY_CHUNK_SIZE.toLocaleString()}. Keep this tab open — the results CSV is built from the responses.`
              : "Keep this tab open — the results CSV is built from the responses."}
          </div>
        </div>
      ) : null}

      {phase === "scanned" && scan ? (
        <>
          <div className={styles.card}>
            <div className={styles.cardTitle}>2 · Review planned import</div>
            <div className={styles.tiles}>
              <div className={`${styles.tile} ${styles.tileNeutral}`}>
                <div className={styles.tileNumber}>{scan.counts.sourceRows}</div>
                <div className={styles.tileLabel}>WordPress rows</div>
              </div>
              <div className={`${styles.tile} ${styles.tileNeutral}`}>
                <div className={styles.tileNumber}>{scan.counts.noMatch}</div>
                <div className={styles.tileLabel}>Not an office or lawyer</div>
              </div>
              <div className={`${styles.tile} ${styles.tileNeutral}`}>
                <div className={styles.tileNumber}>{scan.counts.addressBookOrigin}</div>
                <div className={styles.tileLabel}>Already from Address Book</div>
              </div>
              <div className={`${styles.tile} ${styles.tileNeutral}`}>
                <div className={styles.tileNumber}>{scan.counts.byStatus.exists}</div>
                <div className={styles.tileLabel}>Exists</div>
              </div>
              <div className={`${styles.tile} ${styles.tileNeutral}`}>
                <div className={styles.tileNumber}>{scan.counts.byStatus.alias}</div>
                <div className={styles.tileLabel}>Merged-away terms</div>
              </div>
              <div className={`${styles.tile} ${styles.tileError}`}>
                <div className={styles.tileNumber}>{unusableCount}</div>
                <div className={styles.tileLabel}>Deleted / missing / blank / bad locale</div>
              </div>
              <div className={`${styles.tile} ${styles.tileOk}`}>
                <div className={styles.tileNumber}>{selectedCount}</div>
                <div className={styles.tileLabel}>Selected — will be imported</div>
              </div>
            </div>
          </div>

          <div className={styles.stack}>
            <div className={styles.toolbar}>
              <span className={styles.resultCount} aria-live="polite">
                {selectedCount} of {readyCount} ready row{readyCount === 1 ? "" : "s"} selected
              </span>
            </div>
            <DataTable data={tableRows} columns={columns} emptyMessage="Nothing to import." />
          </div>

          <div className={styles.toolbar}>
            <Button variant="contained" disabled={selectedCount === 0} onClick={downloadPlanned}>
              Download planned import (CSV)
            </Button>
            <span className={styles.rowNote}>
              Saves as <code>name-translations-import-planned-….csv</code>.
            </span>
            <span className={styles.spacer} />
            <Button
              variant="contained"
              disabled={selectedCount === 0 || isBusy}
              onClick={() => setIsConfirming(true)}
            >
              Import ({selectedCount})
            </Button>
          </div>
        </>
      ) : null}

      {phase === "done" && applied ? (
        <>
          {resultCounts("error") > 0 ? (
            <div className={`${styles.banner} ${styles.bannerError}`} role="alert">
              {resultCounts("error")} row{resultCounts("error") === 1 ? "" : "s"} failed to import.
              Download the results CSV before retrying.
            </div>
          ) : null}

          <div className={styles.card}>
            <div className={styles.cardTitle}>Result</div>
            <div className={styles.tiles}>
              {RESULT_TILES.map(({ status, label, tile }) => (
                <div key={status} className={`${styles.tile} ${tile}`}>
                  <div className={styles.tileNumber}>{resultCounts(status)}</div>
                  <div className={styles.tileLabel}>{label}</div>
                </div>
              ))}
            </div>
          </div>

          <div className={styles.toolbar}>
            <Button variant="outlined" onClick={() => void runScan()}>
              Scan again
            </Button>
            <span className={styles.spacer} />
            <Button
              variant="contained"
              disabled={applied.length === 0}
              onClick={() => downloadResultsCsv(applied, new Date())}
            >
              Download results CSV
            </Button>
          </div>
        </>
      ) : null}

      {isConfirming ? (
        <ConfirmationModal
          title="Import name translations"
          message={`${selectedCount} translation${selectedCount === 1 ? "" : "s"} will be written to Address Book.`}
          warningMessage="Existing translations are never overwritten. The results CSV lists every row written and is the record for undoing this run."
          confirmButtonText={`Import ${selectedCount}`}
          backdropClick={false}
          onClose={() => setIsConfirming(false)}
          onConfirm={applyImport}
        />
      ) : null}
    </div>
  );
}
